<template>
  <table v-if="documents.length > 0" :class="$style.corporations">
    <thead>
      <tr
        :class="[
          $style.corporations__list,
          $style['corporations__list--header'],
        ]"
      >
        <th :class="[$style.corporations__cell]">
          <p>資料名</p>
        </th>
        <th :class="[$style.corporations__cell]">
          <p>動画</p>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        :class="[$style.corporations__list]"
        v-for="document in documents"
        :key="document.id"
      >
        <td :class="[$style.corporations__cell]">
          <div
            @click="clickItem(document.id)"
            :class="[
              $style.version,
              { [$style['version--noFile']]: !document.pdfFilePath },
            ]"
          >
            <p :class="$style.version__title">{{ document.title }}</p>
            <p :class="$style.version__date">
              最終更新日: {{ formatDate(document.updatedAt) }}
            </p>
          </div>
        </td>
        <td
          :class="[
            $style.corporations__cell,
            $style['corporations__cell--video'],
          ]"
        >
          <ExternalLink
            @click="clickVideo(document.id)"
            :href="document.youtubeUrl"
            :class="[
              $style.videoLink,
              {
                [$style['videoLink--disabled']]: !document.youtubeUrl,
              },
            ]"
          >
            <p v-if="document.youtubeUrl">動画あり</p>
            <p v-else>動画なし</p>
          </ExternalLink>
        </td>
      </tr>
    </tbody>
  </table>
  <div v-else :class="$style.empty">
    <OkanekoSorry :class="$style.empty__img" />
    <p>該当の資料は見つかりませんでした</p>
  </div>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import ExternalLink from '@/components/atoms/ExternalLink.vue'
import OkanekoSorry from '@/assets/images/okaneko-sorry.svg?component'

export type Document = {
  id: string
  title: string
  pdfFilePath?: string
  youtubeUrl?: string
  updatedAt: string
}

const props = defineProps<{
  documents: Document[]
}>()

const emit = defineEmits<{
  (event: 'click', value: { id: Document['id'] }): void
  (event: 'click-video', value: { id: Document['id'] }): void
}>()

const clickItem = async (id: Document['id']) => {
  const document = props.documents.find(({ id: targetId }) => targetId === id)

  if (document?.pdfFilePath) {
    emit('click', { id })
  }
}

const clickVideo = (id: Document['id']) => {
  const document = props.documents.find(({ id: targetId }) => targetId === id)

  if (document?.youtubeUrl) {
    emit('click-video', { id })
  }
}

const formatDate = (value: string) => format(new Date(value), 'yyyy/M/d H:mm')
</script>

<style lang="scss" module>
.corporations {
  $title-width: 300px;

  border-spacing: 0;
  border: 2px solid $color-border-low-emphasis;
  border-radius: 6px;

  &__cell {
    padding: 10px 20px;
    text-align: left;

    & > p {
      font-size: 16px;
    }

    &--video {
      width: 150px;

      & > p {
        color: $color-text-and-object-accent-primary;
        font-size: 14px;
      }
    }
  }

  &__list {
    align-items: left;

    &:nth-child(n + 2) {
      border-top: 1px solid $color-border-low-emphasis;
    }

    &--header {
      background-color: $color-surface-secondary;
      font-weight: bold;
    }
  }
}

.versions {
  width: 100%;

  li {
    width: 100%;

    &:nth-child(n + 2) {
      margin-top: 6px;
    }
  }
}

.version {
  width: 100%;
  background-color: $color-surface-secondary;
  padding: 8px 20px;
  border-radius: 6px;
  color: $color-text-and-object-accent-primary;
  font-size: 16px;

  &--noFile {
    color: $color-text-and-object-high-emphasis;
  }

  &:hover {
    background-color: $color-surface-accent-primary-light;
  }

  &__date {
    font-size: 10px;
    color: $color-text-and-object-high-emphasis;
    text-align: right;
  }
}

.videoLink {
  color: $color-text-and-object-accent-primary;

  &--disabled {
    color: $color-text-and-object-disable;
  }
}

.empty {
  background-color: $color-surface-secondary;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & > *:nth-child(n + 2) {
    margin-top: 20px;
  }
}
</style>
